"use client"
import { useNavigate } from "react-router-dom"
import "./jobSearch.css"
import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchSuggestionsAsync, selectSuggestions } from "./Suggestions/SuggestionsSlice"
import {
    fetchPublicCoreLocationsAsync,
    selectPublicCoreLocations,
} from "./FetchLocationSlice/FetchLocationSlice"

const JOBLIST_PERSIST_KEY = "jobListPersist"

export function JobSearchRecommended() {
    const suggestions = useSelector(selectSuggestions) || []
    const coreLocation = useSelector(selectPublicCoreLocations) || []
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // Main search (skills/designations/companies)
    const [inputValue, setInputValue] = useState("")
    const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState("")
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [selectedSuggestions, setSelectedSuggestions] = useState([])

    // Experience
    const [selectedExperience, setSelectedExperience] = useState("")

    // Locations (searchable multi-select)
    const [selectedLocations, setSelectedLocations] = useState([]) // [{uuid, title}, ...]
    const [locationQuery, setLocationQuery] = useState("")
    const [debouncedLocationQuery, setDebouncedLocationQuery] = useState("")
    const [showLocationDropdown, setShowLocationDropdown] = useState(false)

    // Refs for click-outside
    const suggestionsRef = useRef(null)
    const inputRef = useRef(null)
    const locationInputRef = useRef(null)
    const locationDropdownRef = useRef(null)

    // Shared field class for consistent sizing
    const fieldClass =
        "h-11 w-full px-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-gray-700 placeholder-gray-400"

    // Debounce main keyword
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchKeyword(inputValue), 300)
        return () => clearTimeout(t)
    }, [inputValue])

    // Fetch suggestions for main search (debounced)
    useEffect(() => {
        if (debouncedSearchKeyword.trim()) {
            dispatch(fetchSuggestionsAsync({ keyword: debouncedSearchKeyword.trim() }))
        }
    }, [debouncedSearchKeyword, dispatch])

    // Debounce location query
    useEffect(() => {
        const t = setTimeout(() => setDebouncedLocationQuery(locationQuery.trim()), 300)
        return () => clearTimeout(t)
    }, [locationQuery])

    // Fetch locations on mount and whenever debounced location query changes
    useEffect(() => {
        dispatch(fetchPublicCoreLocationsAsync({ keyword: debouncedLocationQuery }))
    }, [debouncedLocationQuery, dispatch])

    // Close dropdowns on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            const target = event.target
            const clickedOutsideMain =
                suggestionsRef.current &&
                !suggestionsRef.current.contains(target) &&
                inputRef.current &&
                !inputRef.current.contains(target)
            const clickedOutsideLocation =
                locationDropdownRef.current &&
                !locationDropdownRef.current.contains(target) &&
                locationInputRef.current &&
                !locationInputRef.current.contains(target)

            if (clickedOutsideMain) setShowSuggestions(false)
            if (clickedOutsideLocation) setShowLocationDropdown(false)
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    // Handlers
    const handleInputChange = (e) => {
        setInputValue(e.target.value)
        setShowSuggestions(true)
    }

    const handleSuggestionClick = (suggestion) => {
        if (!selectedSuggestions.some((item) => item.uuid === suggestion.uuid)) {
            setSelectedSuggestions((prev) => [...prev, suggestion])
        }
        setInputValue("")
        setShowSuggestions(false)
    }

    const handleLocationPick = (location) => {
        if (!selectedLocations.some((l) => l.uuid === location.uuid)) {
            // Store both uuid and title so we can build label map for JobList
            setSelectedLocations((prev) => [...prev, { uuid: location.uuid, title: location.title }])
        }
        setLocationQuery("")
        setShowLocationDropdown(false)
    }

    // Enter to pick top location suggestion
    const handleLocationKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault()
            const first = coreLocation.find((loc) => !selectedLocations.some((l) => l.uuid === loc.uuid))
            if (first) handleLocationPick(first)
        }
    }

    const jsUuids = useMemo(
        () =>
            selectedSuggestions
                .filter((item) => item.uuid?.startsWith("js"))
                .map((item) => item.uuid.replace(/^js_/, "")),
        [selectedSuggestions]
    )
    const jtUuids = useMemo(
        () =>
            selectedSuggestions
                .filter((item) => item.uuid?.startsWith("jt"))
                .map((item) => item.uuid.replace(/^jt_/, "")),
        [selectedSuggestions]
    )
    const cnUuids = useMemo(
        () =>
            selectedSuggestions
                .filter((item) => item.uuid?.startsWith("cn"))
                .map((item) => item.uuid.replace(/^cn_/, "")),
        [selectedSuggestions]
    )

    // Helper: robust unwrap for createAsyncThunk
    const runThunk = async (thunk) => {
        const action = dispatch(thunk)
        if (action && typeof action.unwrap === "function") {
            return await action.unwrap()
        }
        const res = await action
        return res?.payload
    }

    const pickAutoSuggestions = (term, pool) => {
        if (!term) return []
        const t = term.toLowerCase()
        const exact = pool.filter((s) => s?.title?.toLowerCase() === t)
        const starts = pool.filter(
            (s) => s?.title?.toLowerCase().startsWith(t) && !exact.some((e) => e.uuid === s.uuid)
        )
        const includes = pool.filter(
            (s) => s?.title?.toLowerCase().includes(t) && !exact.concat(starts).some((e) => e.uuid === s.uuid)
        )
        return [...exact, ...starts, ...includes].slice(0, 3)
    }

    // Submit search (Enter or button)
    const handleSearch = async () => {
        const term = inputValue.trim()

        // If user typed but didn't select, auto-pick top suggestions so JobList can use skills/titles/company
        let pool = suggestions
        if (term && (!Array.isArray(pool) || pool.length === 0)) {
            try {
                const payload = await runThunk(fetchSuggestionsAsync({ keyword: term }))
                pool = payload?.response || payload?.data || payload || []
            } catch {
                pool = suggestions || []
            }
        }
        let toAdd = []
        if (term) {
            const auto = pickAutoSuggestions(term, pool || [])
            toAdd = auto.filter((s) => !selectedSuggestions.some((sel) => sel.uuid === s.uuid))
        }
        const finalSelectedSuggestions = [...selectedSuggestions, ...toAdd]

        // Build arrays for JobList
        const finalJs = finalSelectedSuggestions
            .filter((i) => i.uuid?.startsWith("js"))
            .map((i) => i.uuid.replace(/^js_/, ""))
        const finalJt = finalSelectedSuggestions
            .filter((i) => i.uuid?.startsWith("jt"))
            .map((i) => i.uuid.replace(/^jt_/, ""))
        const finalCn = finalSelectedSuggestions
            .filter((i) => i.uuid?.startsWith("cn"))
            .map((i) => i.uuid.replace(/^cn_/, ""))

        // Extract UUIDs and build UUID->title map to display names in JobList
        const locationUUIDs = selectedLocations.map((l) => l.uuid)
        const locationLabelMap = Object.fromEntries(selectedLocations.map((l) => [l.uuid, l.title]))

        // Clear any persisted job list state so new search isn't overridden
        try {
            sessionStorage.removeItem(JOBLIST_PERSIST_KEY)
        } catch { }

        navigate("/jobs", {
            state: {
                fromJobSearch: true, // JobList prioritizes this state
                keyword: term,
                selectedLocations: locationUUIDs,
                locationLabelMap,     // UUID -> Title mapping for display
                selectedSuggestions: finalSelectedSuggestions,
                skills: finalJs,
                titles: finalJt,
                company: finalCn,
                experience: selectedExperience,
            },
        })
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault()
            handleSearch()
        }
    }

    return (
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[linear-gradient(90deg,#f2f8fc_0%,#fff_30%,#fff_70%,#f2f8fc_100%)]">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-10 text-center">
                    <h1 className="font-bold leading-tight text-[28px] sm:text-[36px] md:text-[44px] lg:text-[52px]">
                        India&apos;s <span className="text-fontBlue">Leading</span> Job Portal!
                    </h1>
                    <p className="mt-3 text-base sm:text-lg text-gray-600">
                        Optimize your hiring process with advanced AI-based search — one solution for both freshers and experienced professionals.
                    </p>
                </div>

                {/* Search Card */}
                <div className="max-w-5xl mx-auto bg-white rounded-3xl sm:rounded-[32px] shadow-[0_0_30px_rgba(28,100,242,0.08),_0_0_60px_rgba(28,100,242,0.04)] p-4 sm:p-6">
                    <form
                        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-stretch"
                        onSubmit={(e) => {
                            e.preventDefault()
                            handleSearch()
                        }}
                    >
                        {/* Main Search */}
                        <div className="relative" ref={inputRef}>
                            <label htmlFor="search" className="sr-only">
                                Search skills/designations/companies
                            </label>
                            <input
                                id="search"
                                className={fieldClass}
                                type="text"
                                placeholder="Enter skills / designations / companies"
                                value={inputValue}
                                onChange={handleInputChange}
                                onFocus={() => setShowSuggestions(true)}
                                onKeyDown={handleKeyDown}
                                autoComplete="off"
                            />
                            {showSuggestions && suggestions.length > 0 && (
                                <div
                                    ref={suggestionsRef}
                                    className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded-md mt-1 shadow-lg z-50 max-h-60 overflow-y-auto"
                                >
                                    {suggestions.map((suggestion, index) => (
                                        <button
                                            key={suggestion.uuid || index}
                                            type="button"
                                            className="w-full text-left px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm"
                                            onClick={() => handleSuggestionClick(suggestion)}
                                        >
                                            <span className="text-gray-700">{suggestion.title}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Experience */}
                        <div className="relative">
                            <label htmlFor="experience" className="sr-only">
                                Select experience
                            </label>
                            <div className="relative">
                                <select
                                    id="experience"
                                    className={`${fieldClass} appearance-none pr-8`}
                                    value={selectedExperience}
                                    onChange={(e) => setSelectedExperience(e.target.value)}
                                >
                                    <option value="">Select experience</option>
                                    <option value="0">Fresher (0 Years)</option>
                                    {Array.from({ length: 30 }, (_, i) => i + 1).map((year) => (
                                        <option key={year} value={year}>
                                            {year} {year > 1 ? "Years" : "Year"}
                                        </option>
                                    ))}
                                </select>

                            </div>
                        </div>

                        {/* Location combobox (searchable) */}
                        <div className="relative" ref={locationInputRef}>
                            <label htmlFor="location-search" className="sr-only">
                                Search city
                            </label>
                            <input
                                id="location-search"
                                className={fieldClass}
                                type="text"
                                placeholder="Search city"
                                value={locationQuery}
                                onChange={(e) => setLocationQuery(e.target.value)}
                                onFocus={() => setShowLocationDropdown(true)}
                                onKeyDown={handleLocationKeyDown}
                                autoComplete="off"
                            />

                            {showLocationDropdown && (
                                <div
                                    ref={locationDropdownRef}
                                    className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded-md mt-1 shadow-lg z-50 max-h-60 overflow-y-auto"
                                >
                                    {coreLocation.length === 0 && debouncedLocationQuery.length > 0 && (
                                        <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
                                    )}
                                    {coreLocation.length === 0 && debouncedLocationQuery.length === 0 && (
                                        <div className="px-3 py-2 text-sm text-gray-500">Type to search city</div>
                                    )}

                                    {coreLocation.map((item) => {
                                        const already = selectedLocations.some((l) => l.uuid === item.uuid)
                                        return (
                                            <button
                                                key={item.uuid}
                                                type="button"
                                                disabled={already}
                                                className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm hover:bg-gray-50 ${already ? "text-gray-400 cursor-not-allowed bg-gray-50" : "text-gray-700 cursor-pointer"
                                                    }`}
                                                onClick={() => !already && handleLocationPick(item)}
                                                title={already ? "Already selected" : "Add location"}
                                            >
                                                {item.title}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            className="h-11 w-full md:w-full px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 outline-none text-sm"
                        >
                            Search
                        </button>
                    </form>
                </div>

                {/* Selected tags */}
                {(selectedSuggestions.length > 0 || selectedLocations.length > 0) && (
                    <div className="max-w-5xl mx-auto flex flex-wrap gap-2 justify-start sm:justify-center mt-3">
                        {selectedSuggestions.map((suggestion) => (
                            <div
                                key={suggestion.uuid}
                                className="inline-flex items-center bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm"
                            >
                                {suggestion.title}
                                <button
                                    className="ml-2 text-green-500 hover:text-green-700 focus:outline-none"
                                    onClick={() =>
                                        setSelectedSuggestions((prev) => prev.filter((s) => s.uuid !== suggestion.uuid))
                                    }
                                    aria-label={`Remove ${suggestion.title}`}
                                    type="button"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                        {selectedLocations.map((location) => (
                            <div
                                key={location.uuid}
                                className="inline-flex items-center bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                            >
                                {location.title}
                                <button
                                    className="ml-2 text-blue-500 hover:text-blue-700 focus:outline-none"
                                    onClick={() =>
                                        setSelectedLocations((prev) => prev.filter((l) => l.uuid !== location.uuid))
                                    }
                                    aria-label={`Remove ${location.title}`}
                                    type="button"
                                >
                                    ×
                                </button>
                            </div>
                        ))}

                        <button
                            type="button"
                            className="ml-1 text-gray-500 hover:text-gray-700 text-sm underline"
                            onClick={() => {
                                setSelectedSuggestions([])
                                setSelectedLocations([])
                                setSelectedExperience("")
                                setLocationQuery("")
                            }}
                        >
                            Clear all
                        </button>
                    </div>
                )}
            </div>
        </section>
    )
}